import { MantineProvider, localStorageColorSchemeManager, useComputedColorScheme } from '@mantine/core';
import { QueryClientProvider } from '@tanstack/react-query';
import { MotionConfig } from 'framer-motion';
import { useEffect } from 'react';
// NOTE: '@mantine/core/styles.css' is imported in main.tsx *before* global.css so
// the Forge token layer and Tailwind utilities win cascade ties. Do not re-add it
// here — that would re-append Mantine after our tokens.
import { AppRouterProvider } from './lib/router';
import { queryClient } from './lib/queryClient';
import { forgeTheme } from './theme/mantine';
import { Toaster } from './components/ui/Toaster';

/**
 * App shell — global providers in the order they must wrap each other:
 *
 *   MantineProvider (theme + CSS variables; light default, toggleable)
 *     QueryClientProvider (server state)
 *       AppRouterProvider (route tree + page chrome)
 *
 * The color scheme is persisted via localStorage and drives BOTH Mantine and
 * the Tailwind CSS-variable tokens (through data-mantine-color-scheme on <html>).
 * Auth bootstrap (Amplify) is initialized once in `main.tsx` before render.
 */
const colorSchemeManager = localStorageColorSchemeManager({ key: 'forge-color-scheme' });

/**
 * Keeps the mobile browser chrome (`<meta name="theme-color">`) in sync with the
 * active color scheme — the static value in index.html can only be right for one
 * scheme. Runs on mount and on every scheme change. The meta `content` must be a
 * literal color (CSS variables are not resolved there), so these hex values track
 * the light background (#ffffff) and dark `--c-void` (#09090b) from global.css.
 */
function ThemeColorMeta() {
  const scheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  useEffect(() => {
    document
      .querySelector('meta[name=theme-color]')
      ?.setAttribute('content', scheme === 'dark' ? '#09090b' : '#ffffff');
  }, [scheme]);
  return null;
}

export default function App() {
  return (
    <MantineProvider
      theme={forgeTheme}
      defaultColorScheme="light"
      colorSchemeManager={colorSchemeManager}
    >
      {/* reducedMotion="user" — all framer-motion entrances honor prefers-reduced-motion in one place. */}
      <MotionConfig reducedMotion="user">
        <QueryClientProvider client={queryClient}>
          <ThemeColorMeta />
          {/* Single global notification surface. Every mutation failure lands
              here via the MutationCache in lib/queryClient.ts; consequential
              successes push explicitly from their call site. */}
          <Toaster />
          <AppRouterProvider />
        </QueryClientProvider>
      </MotionConfig>
    </MantineProvider>
  );
}
